/**
 * Snap helpers for region resize in isolation views.
 * Used by IsolationResizeHandles to snap region edges to fixture boundaries.
 */

/**
 * Find the closest snap line within a threshold.
 * @param {number} value - Current percentage value (0-100)
 * @param {Array<{pct: number, fixtureId: string, edge: string}>} snapLines
 * @param {number} threshold - Snap distance in percentage
 * @returns {{snapped: number, snapLine: object|null}}
 */
export function snapToNearestLine(value, snapLines, threshold) {
  let closest = null;
  let minDist = threshold;
  for (const line of snapLines) {
    const dist = Math.abs(value - line.pct);
    if (dist < minDist) {
      minDist = dist;
      closest = line;
    }
  }
  return closest
    ? { snapped: closest.pct, snapLine: closest }
    : { snapped: value, snapLine: null };
}

/**
 * Convert a fixture bounding box (in pixels) to snap lines (in percentages).
 * @param {{id: string, x: number, y: number, width: number, height: number}} fixture
 * @param {number} totalWidth - Container width in pixels
 * @param {number} totalHeight - Container height in pixels
 * @returns {{vertical: Array, horizontal: Array}}
 */
export function fixtureToSnapLines(fixture, totalWidth, totalHeight) {
  const left = clamp((fixture.x / totalWidth) * 100);
  const right = clamp(((fixture.x + fixture.width) / totalWidth) * 100);
  const top = clamp((fixture.y / totalHeight) * 100);
  const bottom = clamp(((fixture.y + fixture.height) / totalHeight) * 100);
  const id = fixture.id;
  return {
    vertical: [
      { pct: left, fixtureId: id, edge: 'left' },
      { pct: right, fixtureId: id, edge: 'right' },
    ],
    horizontal: [
      { pct: top, fixtureId: id, edge: 'top' },
      { pct: bottom, fixtureId: id, edge: 'bottom' },
    ],
  };
}

/**
 * Ids of fixtures whose edges sit INSIDE another fixture's — the outermost
 * boundary is the one worth snapping to, so these are skipped:
 *   - a bathtub with a `tub-support-box` / `tub-surface` child (the deck is
 *     the outer edge);
 *   - a countertop's parent cabinets and its child sinks.
 *
 * Shared by the floor's snap lines (`fixtureToSnapLinesWorld`) and the wall's
 * (`wallRegionSnapLines`), so the two surfaces agree on what a vanity's or a
 * tub's edge is.
 *
 * @param {Array} objects - Room objects
 * @param {Function} getObjectType - Maps obj → type string
 * @returns {Set<string>}
 */
export function innerFixtureIds(objects, getObjectType) {
  const skipIds = new Set();
  if (!objects?.length) return skipIds;

  // Bathtubs with tub-support-box child → skip the bathtub
  const tubSupportTypes = new Set(['tub-support-box', 'tub-surface']);
  for (const obj of objects) {
    const t = getObjectType(obj) || '';
    if (tubSupportTypes.has(t) && obj.parents?.length) {
      for (const p of obj.parents) {
        if (p.type === 'bathtub') skipIds.add(p.id);
      }
    }
  }

  // Countertops → skip parent cabinets and child sinks
  for (const obj of objects) {
    const t = getObjectType(obj) || '';
    if (t === 'countertop') {
      if (obj.parents?.length) {
        for (const p of obj.parents) skipIds.add(p.id);
      }
      for (const other of objects) {
        if (other.parents?.some((p) => p.id === obj.id)) {
          skipIds.add(other.id);
        }
      }
    }
  }
  return skipIds;
}

// Which axes each handle moves — a corner moves both.
const HANDLES_MOVING_X = new Set([
  'left',
  'right',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
]);
const HANDLES_MOVING_Y = new Set([
  'top',
  'bottom',
  'topLeft',
  'topRight',
  'bottomLeft',
  'bottomRight',
]);

/**
 * Snap a dragged handle's coordinates to the nearest snap line on each axis
 * the handle moves.
 *
 * `snapLines.thresholdPct` (`{x, y}`) is the snap distance per axis, in
 * percent of THAT axis — the wall's snap lines carry one, converted from
 * inches with the wall's real length and height, so the pull is the same
 * physical distance along and up a wall of any size. Lines without one (the
 * floor's) use `defaultThresholdPct` on both axes, as they always have.
 *
 * @param {string} handleType - 'left' | 'right' | 'top' | 'bottom' | corner
 * @param {{x: number, y: number}} coords - handle position in percent
 * @param {{vertical?: Array, horizontal?: Array, thresholdPct?: {x, y}}|null} snapLines
 * @param {number} defaultThresholdPct
 * @returns {{x: number, y: number, snapX: object|null, snapY: object|null}}
 *   the (possibly) snapped coordinates and the line each axis snapped to
 */
export function snapHandleCoords(
  handleType,
  coords,
  snapLines,
  defaultThresholdPct
) {
  let { x, y } = coords;
  let snapX = null;
  let snapY = null;
  if (!snapLines) return { x, y, snapX, snapY };
  const thresholdX = snapLines.thresholdPct?.x ?? defaultThresholdPct;
  const thresholdY = snapLines.thresholdPct?.y ?? defaultThresholdPct;

  if (snapLines.vertical?.length && HANDLES_MOVING_X.has(handleType)) {
    const { snapped, snapLine } = snapToNearestLine(
      x,
      snapLines.vertical,
      thresholdX
    );
    x = snapped;
    snapX = snapLine;
  }
  if (snapLines.horizontal?.length && HANDLES_MOVING_Y.has(handleType)) {
    const { snapped, snapLine } = snapToNearestLine(
      y,
      snapLines.horizontal,
      thresholdY
    );
    y = snapped;
    snapY = snapLine;
  }
  return { x, y, snapX, snapY };
}

/**
 * Convert room objects to snap lines using world coordinates + floor bounding box.
 * Filters ceiling/wall-mounted objects and skips inner objects (bathtubs with
 * support boxes, cabinets under countertops).
 *
 * @param {Array} objects - Room objects array (each has center, dimensions, rotation, parents)
 * @param {{minX: number, maxX: number, minZ: number, maxZ: number}} floorBbox
 * @param {Function} getObjectType - Maps obj → type string
 * @param {Function} isCeilingMounted - Returns true for ceiling-mounted types
 * @param {Function} isWallMounted - Returns true for wall-mounted types
 * @returns {{vertical: Array, horizontal: Array}}
 */
export function fixtureToSnapLinesWorld(
  objects,
  floorBbox,
  getObjectType,
  isCeilingMounted,
  isWallMounted
) {
  if (!objects?.length || !floorBbox) return { vertical: [], horizontal: [] };

  const floorWidth = floorBbox.maxX - floorBbox.minX;
  const floorDepth = floorBbox.maxZ - floorBbox.minZ;
  if (floorWidth <= 0 || floorDepth <= 0)
    return { vertical: [], horizontal: [] };

  const skipIds = innerFixtureIds(objects, getObjectType);

  const vertical = [];
  const horizontal = [];

  for (const obj of objects) {
    if (!obj.center || !obj.dimensions) continue;
    if (skipIds.has(obj.id)) continue;

    const objType = getObjectType(obj) || '';
    if (isCeilingMounted(objType, obj) || isWallMounted(objType, obj)) continue;

    const cx = obj.center.x ?? obj.center[0] ?? 0;
    const cz = obj.center.z ?? obj.center[2] ?? 0;
    const localDx = obj.dimensions.x ?? obj.dimensions[0] ?? 0.5;
    const localDz = obj.dimensions.z ?? obj.dimensions[2] ?? 0.5;

    // Account for Y-rotation: axis-aligned bounding box of rotated footprint
    const rotY = obj.rotation?.y ?? 0;
    const cosR = Math.abs(Math.cos(rotY));
    const sinR = Math.abs(Math.sin(rotY));
    const dx = localDx * cosR + localDz * sinR;
    const dz = localDx * sinR + localDz * cosR;

    // Convert to percentage relative to floor bounding box
    const leftPct = clamp(((cx - dx / 2 - floorBbox.minX) / floorWidth) * 100);
    const rightPct = clamp(((cx + dx / 2 - floorBbox.minX) / floorWidth) * 100);
    const topPct = clamp(((cz - dz / 2 - floorBbox.minZ) / floorDepth) * 100);
    const bottomPct = clamp(
      ((cz + dz / 2 - floorBbox.minZ) / floorDepth) * 100
    );

    // Skip tiny objects (less than 1% of floor in both dimensions)
    if (rightPct - leftPct < 1 && bottomPct - topPct < 1) continue;

    const id = obj.id;
    vertical.push(
      { pct: leftPct, fixtureId: id, edge: 'left' },
      { pct: rightPct, fixtureId: id, edge: 'right' }
    );
    horizontal.push(
      { pct: topPct, fixtureId: id, edge: 'top' },
      { pct: bottomPct, fixtureId: id, edge: 'bottom' }
    );
  }

  return { vertical, horizontal };
}

function clamp(v) {
  return Math.max(0, Math.min(100, v));
}
